/*
  File: src/components/store/ProductCard.jsx
  Purpose: Futuristic product tile showing image, name, vendor/brand, and price.
  Props: { product, onClick, vendorName?, variant? ('grid' | 'list') }
*/
import React, { useState } from 'react';

/**
 * ProductCard
 * Clickable product card for grids and lists with neon/glass styling.
 */
function ProductCard({ product, onClick, vendorName, variant = 'grid', priority = false }) {
  const isList = variant === 'list';
  const color = product.color || null;
  const baseName = product.displayName || product.name;
  const title = color ? `${baseName} - ${color}` : baseName;
  const sizes = Array.isArray(product.sizes) ? product.sizes : [];
  const normalizeSizesOrder = (arr) => {
    const values = arr.map(s => String(s).trim());
    const numOrder = ['40', '41', '42', '43'];
    const alphaOrder = ['S', 'M', 'L', 'XL'];
    const comboOrder = ['S/M', 'M/L', 'L/XL'];
    const score = (s) => {
      if (comboOrder.includes(s)) return 200 + comboOrder.indexOf(s);
      if (numOrder.includes(s)) return 100 + numOrder.indexOf(s);
      if (alphaOrder.includes(s)) return 0 + alphaOrder.indexOf(s);
      return 1000 + values.indexOf(s);
    };
    return [...new Set(values)].sort((a, b) => score(a) - score(b));
  };
  const orderedSizes = normalizeSizesOrder(sizes);
  return (
    <div
      className={`group relative ${isList ? 'flex items-stretch p-4' : 'flex flex-col p-4'} bg-white/5 border border-white/10 rounded-2xl hover:bg-white/10 hover:border-white/20 transition-all duration-300 cursor-pointer hover:transform hover:-translate-y-2 focus:outline-none ${isList ? 'min-h-[160px]' : 'min-h-[340px]'}`}
      onClick={onClick}
      style={{ minHeight: isList ? 160 : 340, height: '100%' }}
    >
      <div className={`${isList ? 'w-32 sm:w-36 md:w-40' : 'w-full'} ${isList ? '' : 'mb-4'} aspect-[3/4] bg-gradient-to-br from-slate-600 to-slate-700 rounded-xl overflow-hidden relative flex-shrink-0`}>
        {product.imageUrl ? (
          <ProductImageWithBg
            src={product.imageUrl}
            alt={title}
            name={product.name}
            priority={priority}
          />
        ) : (
          <div className="absolute inset-0 bg-white/10" />
        )}
      </div>

      {isList ? (
        <div className="flex-1 pl-4 flex items-stretch gap-3">
          {/* Middle content */}
          <div className="flex-1 min-w-0 flex flex-col justify-between">
            <div className="min-w-0">
              <div className="text-white font-semibold leading-tight text-base line-clamp-2">{title}</div>
              <div className="text-slate-400 text-sm truncate">
                {(vendorName || '—')}{product.brand ? ` • ${product.brand}` : ''}
              </div>
              {(color || (sizes && sizes.length)) && (
                <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                  {color && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full border border-white/15 bg-white/10 text-white/80">
                      {color}
                    </span>
                  )}
                  {sizes && sizes.length > 0 && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-white/15 bg-white/10 text-white/80 max-w-full">
                      <span className="opacity-70">Sizes:</span>
                      <span className="truncate">{orderedSizes.join(', ')}</span>
                    </span>
                  )}
                </div>
              )}
            </div>
            <div className="text-[11px] text-cyan-200/80 hidden sm:block">Fast shipping • In stock</div>
          </div>
          
          {/* Right side: price */}
          <div className="shrink-0 flex flex-col items-end justify-between min-w-[96px]">
            <span className="inline-flex items-center px-2.5 py-1 rounded-full border border-emerald-400/30 bg-emerald-500/15 text-emerald-200 text-xs font-semibold">
              {product.currency} {(Number(product.price_cents || product.price) / 100).toFixed(2)}
            </span>
            <div className="flex items-center gap-2">
              <div className="text-[11px] text-cyan-200/80 sm:hidden mt-2">Fast shipping • In stock</div>
              
              {/* Linked Avatar (Character or Player) - List View */}
              {(product.character || product.player) && (
                <div className="group/character relative">
                  <img
                    src={(product.character?.image_url || product.player?.image_url) || ''}
                    alt={(product.character?.display_name || product.player?.display_name) || 'Related'}
                    className="w-6 h-6 rounded-full border border-white/20 bg-white/10 object-cover hover:border-white/40 transition-all duration-200 cursor-help"
                  />
                  
                  {/* Tooltip */}
                  <div className="absolute bottom-full right-0 mb-2 opacity-0 group-hover/character:opacity-100 transition-opacity duration-200 pointer-events-none z-10">
                    <div className="bg-gray-900/95 backdrop-blur-sm text-white text-xs px-2 py-1 rounded-lg whitespace-nowrap border border-white/10">
                      {product.character?.display_name || product.player?.display_name}
                      <div className="absolute top-full right-2 w-0 h-0 border-l-[4px] border-r-[4px] border-t-[4px] border-l-transparent border-r-transparent border-t-gray-900/95"></div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className={`flex flex-col justify-between flex-1`} style={{ minHeight: 120 }}>
          <div className="flex flex-col gap-1 flex-1 justify-start">
            <div className="text-white font-semibold break-words whitespace-normal leading-tight text-base" style={{ wordBreak: 'break-word' }}>{title}</div>
            <div className="text-slate-400 text-sm whitespace-nowrap overflow-hidden text-ellipsis">
              {(vendorName || '—')}{product.brand ? ` • ${product.brand}` : ''}
            </div>
          </div>
          <div className="shrink-0 mt-2 flex items-center justify-between">
            <span className="inline-flex items-center px-2.5 py-1 rounded-full border border-emerald-400/30 bg-emerald-500/15 text-emerald-200 text-xs font-semibold">
              {product.currency} {(Number(product.price_cents || product.price) / 100).toFixed(2)}
            </span>
            
            {/* Linked Avatar (Character or Player) - Grid View (bottom-right of card) */}
            {(product.character || product.player) && (
              <div className="group/character relative">
                <img
                  src={(product.character?.image_url || product.player?.image_url) || ''}
                  alt={(product.character?.display_name || product.player?.display_name) || 'Related'}
                  className="w-6 h-6 rounded-full border border-white/20 bg-white/10 object-cover hover:border-white/40 transition-all duration-200 cursor-help"
                />
                
                {/* Tooltip */}
                <div className="absolute bottom-full right-0 mb-2 opacity-0 group-hover/character:opacity-100 transition-opacity duration-200 pointer-events-none z-10">
                  <div className="bg-gray-900/95 backdrop-blur-sm text-white text-xs px-2 py-1 rounded-lg whitespace-nowrap border border-white/10">
                    {product.character?.display_name || product.player?.display_name}
                    <div className="absolute top-full right-2 w-0 h-0 border-l-[4px] border-r-[4px] border-t-[4px] border-l-transparent border-r-transparent border-t-gray-900/95"></div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ProductImageWithBg({ src, alt, name, priority = false }) {
  const [loaded, setLoaded] = useState(false);
  return (
    <div className="w-full h-full flex items-center justify-center overflow-hidden" style={{ background: 'rgba(255,255,255,0.6)' }}>
      {!loaded && (
        <div className="absolute inset-0 animate-pulse bg-white/10" aria-hidden="true" />
      )}
      <img
        src={src}
        alt={alt}
        className={`block w-full h-full object-contain transition duration-500 group-hover:scale-105 ${loaded ? 'opacity-100' : 'opacity-0'}`}
        style={{ background: 'transparent' }}
        loading={priority ? 'eager' : 'lazy'}
        fetchpriority={priority ? 'high' : 'low'}
        decoding="async"
        onLoad={() => setLoaded(true)}
        onError={e => { e.currentTarget.src = `${process.env.PUBLIC_URL}/logo512.png`; setLoaded(true); }}
      />
    </div>
  );
}

export default ProductCard;


